package er.prateek.crossly.ui.share

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.challenge_accept
import crossly.shared.generated.resources.challenge_continue
import crossly.shared.generated.resources.challenge_continue_title
import crossly.shared.generated.resources.challenge_dont_ask
import crossly.shared.generated.resources.challenge_invite_body
import crossly.shared.generated.resources.challenge_later
import crossly.shared.generated.resources.challenge_missing_puzzle
import crossly.shared.generated.resources.challenge_offer_score
import crossly.shared.generated.resources.challenge_offer_time
import crossly.shared.generated.resources.challenge_offer_title
import crossly.shared.generated.resources.challenge_own_app_share
import crossly.shared.generated.resources.challenge_own_link
import crossly.shared.generated.resources.challenge_own_result
import crossly.shared.generated.resources.challenge_play_more
import crossly.shared.generated.resources.challenge_result_time
import crossly.shared.generated.resources.challenge_result_won
import crossly.shared.generated.resources.challenge_share
import crossly.shared.generated.resources.challenge_start_fresh
import crossly.shared.generated.resources.common_cancel
import crossly.shared.generated.resources.common_ok
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.share.ShareOverlay
import er.prateek.crossly.share.formatSpokenDuration
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

@Composable
fun ShareOverlayHost() {
    val share = AppContainer.shareManager
    val ui by share.ui.collectAsStateWithLifecycle()
    val scope = rememberCoroutineScope()
    val colors = MaterialTheme.game

    LaunchedEffect(ui.overlay) {
        if (ui.overlay is ShareOverlay.ChallengeOffer || ui.overlay is ShareOverlay.ChallengeContinue) {
            share.refreshOverlayWithLabels()
        }
    }

    when (val overlay = ui.overlay) {
        ShareOverlay.None -> Unit
        ShareOverlay.NamePrompt -> NamePromptDialog(
            initialName = share.playerName(),
            skipAllowed = true,
            onSave = { share.savePlayerName(it); share.onNameSaved() },
            onSkip = { share.skipNamePrompt() },
            onDismiss = { share.skipNamePrompt() },
        )
        is ShareOverlay.OwnChallenge -> TwoActionDialog(
            title = stringResource(Res.string.challenge_own_link),
            confirm = stringResource(Res.string.challenge_share),
            onConfirm = { share.shareOwnUrl(overlay.url, challenge = true); share.dismissOverlay() },
            onDismiss = share::dismissOverlay,
        )
        is ShareOverlay.OwnAppShare -> TwoActionDialog(
            title = stringResource(Res.string.challenge_own_app_share),
            confirm = stringResource(Res.string.challenge_share),
            onConfirm = { share.shareOwnUrl(overlay.url, challenge = false); share.dismissOverlay() },
            onDismiss = share::dismissOverlay,
        )
        is ShareOverlay.OwnResult -> TwoActionDialog(
            title = stringResource(Res.string.challenge_own_result),
            confirm = stringResource(Res.string.challenge_share),
            onConfirm = { share.shareOwnResultUrl(overlay.url); share.dismissOverlay() },
            onDismiss = share::dismissOverlay,
        )
        is ShareOverlay.FriendInvite -> TwoActionDialog(
            title = stringResource(Res.string.challenge_invite_body, overlay.name),
            confirm = stringResource(Res.string.common_ok),
            onConfirm = share::dismissOverlay,
            onDismiss = share::dismissOverlay,
            showDismiss = false,
        )
        is ShareOverlay.ChallengeOffer -> AlertDialog(
            onDismissRequest = { share.later() },
            containerColor = colors.card,
            shape = RoundedCornerShape(24.dp),
            title = {
                Text(challengeOfferCopy(overlay.challengerName, overlay.puzzleLabel, overlay.bestTimeMs, overlay.bestScore), color = colors.ink)
            },
            confirmButton = {
                Column(modifier = Modifier.fillMaxWidth()) {
                    GameButton(
                        text = stringResource(Res.string.challenge_accept),
                        onClick = { scope.launch { share.acceptChallenge() } },
                        style = GameButtonStyle.PRIMARY,
                        fillWidth = true,
                    )
                    if (!AppContainer.progressionManager.hasAnyProgress() &&
                        AppContainer.savedSessionManager.session == null
                    ) {
                        GameButton(
                            text = stringResource(Res.string.challenge_start_fresh),
                            onClick = { share.startFresh() },
                            style = GameButtonStyle.SECONDARY,
                            fillWidth = true,
                            modifier = Modifier.padding(top = Spacing.sm),
                        )
                    }
                    TextButton(onClick = { share.later() }, modifier = Modifier.fillMaxWidth()) {
                        Text(stringResource(Res.string.challenge_later), color = colors.inkMuted)
                    }
                }
            },
            dismissButton = {},
        )
        is ShareOverlay.ChallengeContinue -> AlertDialog(
            onDismissRequest = { share.dismissOverlay() },
            containerColor = colors.card,
            shape = RoundedCornerShape(24.dp),
            title = {
                Text(stringResource(Res.string.challenge_continue_title), color = colors.ink)
            },
            text = {
                Text(
                    challengeOfferCopy(overlay.challengerName, overlay.puzzleLabel, overlay.bestTimeMs, overlay.bestScore),
                    color = colors.inkMuted,
                )
            },
            confirmButton = {
                Column(modifier = Modifier.fillMaxWidth()) {
                    GameButton(
                        text = stringResource(Res.string.challenge_continue),
                        onClick = { scope.launch { share.continueChallenge() } },
                        style = GameButtonStyle.PRIMARY,
                        fillWidth = true,
                    )
                    if (!AppContainer.progressionManager.hasAnyProgress() &&
                        AppContainer.savedSessionManager.session == null
                    ) {
                        GameButton(
                            text = stringResource(Res.string.challenge_start_fresh),
                            onClick = { share.startFresh() },
                            style = GameButtonStyle.SECONDARY,
                            fillWidth = true,
                            modifier = Modifier.padding(top = Spacing.sm),
                        )
                    }
                    TextButton(onClick = { share.dontAskAgain() }, modifier = Modifier.fillMaxWidth()) {
                        Text(stringResource(Res.string.challenge_dont_ask), color = colors.inkMuted)
                    }
                }
            },
            dismissButton = {},
        )
        is ShareOverlay.FriendResult -> AlertDialog(
            onDismissRequest = share::dismissOverlay,
            containerColor = colors.card,
            shape = RoundedCornerShape(24.dp),
            title = {
                Text(
                    text = if (overlay.won) {
                        stringResource(
                            Res.string.challenge_result_won,
                            overlay.friendName,
                            formatSpokenDuration(overlay.marginMs),
                        )
                    } else {
                        overlay.friendName
                    },
                    color = colors.ink,
                )
            },
            text = {
                Text(
                    stringResource(Res.string.challenge_result_time, formatSpokenDuration(overlay.friendTimeMs)),
                    color = colors.inkMuted,
                )
            },
            confirmButton = {
                GameButton(
                    text = stringResource(Res.string.challenge_play_more),
                    onClick = { share.playAndChallengeMore() },
                    style = GameButtonStyle.PRIMARY,
                    fillWidth = true,
                    modifier = Modifier.fillMaxWidth(),
                )
            },
            dismissButton = {},
        )
        is ShareOverlay.PuzzleMissing -> TwoActionDialog(
            title = stringResource(Res.string.challenge_missing_puzzle, overlay.challengerName),
            confirm = stringResource(Res.string.common_ok),
            onConfirm = share::dismissOverlay,
            onDismiss = share::dismissOverlay,
            showDismiss = false,
        )
    }
}

@Composable
private fun TwoActionDialog(
    title: String,
    confirm: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    showDismiss: Boolean = true,
) {
    val colors = MaterialTheme.game
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = colors.card,
        shape = RoundedCornerShape(24.dp),
        title = { Text(title, color = colors.ink) },
        confirmButton = {
            GameButton(
                text = confirm,
                onClick = onConfirm,
                style = GameButtonStyle.PRIMARY,
                size = GameButtonSize.SMALL,
            )
        },
        dismissButton = if (showDismiss) {
            {
                GameButton(
                    text = stringResource(Res.string.common_cancel),
                    onClick = onDismiss,
                    style = GameButtonStyle.GHOST,
                    size = GameButtonSize.SMALL,
                )
            }
        } else {
            null
        },
    )
}

@Composable
private fun challengeOfferCopy(challengerName: String, puzzleLabel: String, bestTimeMs: Long, bestScore: Int = 0): String {
    val headline = stringResource(Res.string.challenge_offer_title, challengerName, puzzleLabel)
    if (bestTimeMs <= 0L) return headline
    val time = stringResource(Res.string.challenge_offer_time, formatSpokenDuration(bestTimeMs))
    // Riddle challenges are won on time *and* correct answers, so say both up front.
    val score = if (bestScore > 0) " ${stringResource(Res.string.challenge_offer_score, bestScore)}" else ""
    return "$headline $time$score"
}
